import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Col, Dropdown, Form, Modal, Row } from "react-bootstrap";
import { Task } from "../state/Task";
import Icon from './Icon';
import {
    faList as listOutline,
    faTrash as trashBin,
    faClock as timeOutline,
    faEdit as pencil,
    faCheckCircle as checkmarkCircle,
    faHistory as historyIcon,
    faListAlt as listAlt,
    faStickyNote as stickyNote
} from '@fortawesome/free-solid-svg-icons';
import { deadlineColor } from "../state/helpers";
import { format, parse } from "date-fns";
import { ru } from "date-fns/locale";
import tinycolor from "tinycolor2";
import { MDXEditorMethods } from "@mdxeditor/editor";
import { ForwardRefEditor } from "./markdown/ForwardRefEditor";
import { TaskModalProps } from "./plan/context";
import { useTaskModal } from "./TaskModalContext";
import { useData, useTaskChecklistItems } from "../storage/accessors";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ScheduleTab from "./taskSchedule";
import classNames from "classnames";
import TaskTrackerWidget from "./task/taskTrackerWidget";
import TaskChecklist from "./task/taskChecklist";
import TaskComments from "./task/taskComments";
import ButtonDatePicker from "./datePicker";

export const TaskForm: React.FC<{ task: Task, editRoleId?: string, onRoleChange?: (roleId: string) => void, afterSave?: () => void, onCancel?: () => void }> = ({ task, editRoleId, onRoleChange, afterSave, onCancel }) => {
    const [description, setDescription] = useState(task.description || "");
    const [deadline, setDeadline] = useState(task.deadline || "");
    const inputRef = useRef<HTMLInputElement>(null);
    const contextEditor = React.useRef<MDXEditorMethods>(null);
    const { tasks } = useData();

    useEffect(() => {
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }, 100);
    }, []);

    const editor = useMemo(() => {
        return <ForwardRefEditor ref={contextEditor} markdown={task.context || ""} className="lh-1" />
    }, [])

    const save = () => {
        // Сохраняем часть только при нажатии кнопки "Сохранить"
        tasks.save(task.uuid, {
            description,
            deadline: deadline || undefined,
            context: contextEditor.current?.getMarkdown() || "",
            lifepart: editRoleId
        });
        if (afterSave) {
            afterSave()
        }
    }

    const cancel = () => {
        if (onCancel) {
            onCancel();
        }
    }

    return <>
        <Form.Group>
            <Form.Label>Задача</Form.Label>
            <Form.Control value={description} onChange={(e: any) => setDescription(e.target.value)} ref={inputRef} />
        </Form.Group>
        <Form.Group className="mt-3">
            <Form.Label>Дедлайн</Form.Label>
            <div className="d-flex align-items-center gap-2">
                <ButtonDatePicker
                    date={deadline ? parse(deadline, "yyyy-MM-dd", new Date()) : new Date()}
                    onSelect={(date) => setDeadline(date ? format(date, "yyyy-MM-dd") : "")}
                    icon={pencil}
                    title="Установить дедлайн"
                    inputProps={{ variant: "outline-secondary" }}
                />
                {deadline && (
                    <>
                        <span className="text-muted">
                            {format(parse(deadline, "yyyy-MM-dd", new Date()), "dd MMMM yyyy", { locale: ru })}
                        </span>
                        <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => setDeadline("")}
                            title="Убрать дедлайн"
                        >
                            <Icon icon={trashBin} />
                        </Button>
                    </>
                )}
            </div>
            <Form.Text className="text-muted">
                Укажите дату, к которой задача должна быть выполнена
            </Form.Text>
        </Form.Group>
        <Form.Group>
            <Form.Label>Заметка<br /></Form.Label>
            <div className="lh-1">
                {editor}
            </div>
            {/* <Input type="textarea" rows={10} style={{ fontSize: "small" }} defaultValue={context} onBlur={(e: any) => setContext(e.target.value)} /> */}
            <Form.Label><small className="text-secondary">Текущий статус, прочитав который Вы вспомните что нужно дальше делать для завершения задачи</small></Form.Label>
        </Form.Group>
        <div className="d-flex gap-2 mt-3">
            <Button size="sm" variant="success" onClick={save}>Сохранить</Button>
            <Button size="sm" variant="outline-secondary" onClick={cancel}>Отмена</Button>
        </div>
    </>

}

export default function TaskModal({ taskUUID, fromDate, toggle, onOpenTask }: TaskModalProps & { onOpenTask?: (taskUuid: string, fromDate?: Date) => void }) {
    const { tasks, roles } = useData();
    const task = tasks.objects[taskUUID];
    const checklistItems = useTaskChecklistItems(taskUUID);
    const { setTaskModal } = useTaskModal() || {};
    const [editMode, setEditMode] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState(task.lifepart || "");
    const [editRoleId, setEditRoleId] = useState(task.lifepart || "");

    // Синхронизация selectedRoleId при смене задачи
    useEffect(() => {
        setSelectedRoleId(task.lifepart || "");
    }, [taskUUID, task.lifepart]);

    // Синхронизация editRoleId при входе/выходе из режима редактирования
    useEffect(() => {
        if (editMode) {
            setEditRoleId(selectedRoleId);
        }
    }, [editMode]);

    const part = roles.objects[selectedRoleId || ""];
    const parents = useMemo<Task[]>(() => {
        let parentsList = [];
        let current = task;
        while (current && current.parentTask) {
            current = tasks.objects[current.parentTask];
            parentsList.push(current)
        }
        parentsList.reverse();
        return parentsList;
    }, [tasks, task?.uuid])

    const handleOpenParentTask = (parentTaskUuid: string) => {
        if (setTaskModal) {
            setTaskModal({ taskUUID: parentTaskUuid, fromDate });
        } else if (onOpenTask) {
            onOpenTask(parentTaskUuid, fromDate);
        }
    };

    const finalToggle = () => {
        if ((editMode && confirm('Закрыть карточку без сохранения?')) || !editMode) {
            if (toggle)
                toggle();
        }
    };



    if (!task) {
        return <Modal show={true} size={"xl"}>
            <Modal.Header>Загрузка...</Modal.Header>
        </Modal>
    }

    const color = part?.color || "#FFFFFF";

    const isLightColor = tinycolor(color).isLight();
    const headerStyle = part ? { backgroundColor: color, color: isLightColor ? "black" : "white" } : {};

    return (
        <Modal show={true} onHide={finalToggle} size={"xl"} contentClassName="shadow" autoFocus={false} enforceFocus={false}>
            <Modal.Header style={headerStyle}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <span>{task.description}</span>
                    <div className="d-flex align-items-center gap-2" style={isLightColor ? { color: "black" } : { color: "white" }}>
                        <Badge bg={task.isDone ? "success" : "secondary"}>{task.isDone ? "Завершена" : "Не завершена"}</Badge>
                        <Button
                            variant="close"
                            aria-label="Close"
                            onClick={finalToggle}
                            className={classNames({ 'text-bg-light': !isLightColor, "text-bg-dark": isLightColor })}
                        />
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body style={{ overflowY: 'scroll' }}>
                <Row className="mb-2">
                    <Col>
                        {/* Виджет выбора баланса - только в режиме редактирования */}
                        {editMode && (
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <span className="text-secondary"></span>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="outline-secondary"
                                        size="sm"
                                        className="d-flex align-items-center gap-1"
                                    >
                                        <span
                                            className="d-inline-block rounded"
                                            style={{
                                                width: 12,
                                                minWidth: 12,
                                                height: 12,
                                                backgroundColor: roles.objects[editRoleId]?.color || '#cccccc'
                                            }}
                                        />
                                        {roles.objects[editRoleId]?.name || "-"}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {Object.values(roles.objects)
                                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                                            .map((role, index) => (
                                                <Dropdown.Item
                                                    key={role.uuid}
                                                    onClick={() => setEditRoleId(role.uuid)}
                                                    active={editRoleId === role.uuid}
                                                >
                                                    <span
                                                        className="d-inline-block me-2 rounded"
                                                        style={{
                                                            width: 12,
                                                            minWidth: 12,
                                                            height: 12,
                                                            backgroundColor: role.color || '#cccccc'
                                                        }}
                                                    />
                                                    #{index + 1} {role.name}
                                                </Dropdown.Item>
                                            ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        )}

                        {parents.length > 0 && <>
                            <h5 className="mb-3"><Icon icon={listAlt} className="me-2" />Родительские задачи</h5>
                            <ul>
                                {parents.map((parentTask, index) => <li key={parentTask.uuid} style={{ cursor: "pointer", marginLeft: Math.min(100, 20 * index) }} className="text-decoration-underline" onClick={() => handleOpenParentTask(parentTask.uuid)}>{parentTask.description}</li>)}

                            </ul>
                        </>}
                        {!editMode && <>
                            <div className="d-flex gap-2 mt-3 mb-3">
                                <Button size="sm" variant="outline-primary" onClick={() => setEditMode(true)}>
                                    <Icon icon={pencil} className="me-1" /> Редактировать
                                </Button>
                                {!task.isDone && (
                                    <Button
                                        size="sm"
                                        variant="outline-success"
                                        onClick={() => tasks.save(task.uuid, { isDone: true })}
                                    >
                                        <Icon icon={checkmarkCircle} className="me-1" /> Завершить
                                    </Button>
                                )}
                                {task.isDone && (
                                    <Button
                                        size="sm"
                                        variant="outline-warning"
                                        onClick={() => tasks.save(task.uuid, { isDone: false })}
                                    >
                                        <Icon icon={historyIcon} className="me-1" /> Открыть повторно
                                    </Button>
                                )}
                            </div>
                            {task.context !== "" && <>
                                <h5 className="mb-3"><Icon icon={stickyNote} className="me-2" />Заметка:</h5>
                                <div className="markdown-content" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                                    <Markdown remarkPlugins={[remarkGfm]}>{task.context}</Markdown>
                                </div>
                            </>
                            }
                            {/* Дедлайн - отображение в режиме просмотра */}
                            {task.deadline && !task.isDone && (
                                <div className="mt-3 mb-3">
                                    <h5 className="mb-2"><Icon icon={timeOutline} className="me-2" />Дедлайн:</h5>
                                    <Badge bg={deadlineColor(task.deadline)} className="p-2">
                                        {format(parse(task.deadline, "yyyy-MM-dd", new Date()), "dd MMMM yyyy", { locale: ru })}
                                    </Badge>
                                </div>
                            )}
                        </>}
                        {editMode && <TaskForm task={task} editRoleId={editRoleId} afterSave={() => {
                            setSelectedRoleId(editRoleId);
                            setEditMode(false);
                        }} onCancel={() => {
                            setEditRoleId(selectedRoleId);
                            setEditMode(false);
                        }} />}

                        {/* Секция Трекеры - показываем только в режиме просмотра */}
                        {!editMode && part && (
                            <TaskTrackerWidget
                                taskTracker={task.tracker}
                                lifePart={part}
                                onTrackerChange={(trackerUuid) => tasks.save(task.uuid, { tracker: trackerUuid })}
                                onCloseModal={finalToggle}
                                hideEditButton
                            />
                        )}

                    </Col>
                </Row>
                <Row>
                    <Col>
                        {/* Расписание - всегда видно */}
                        <h5 className="mb-3"><Icon icon={timeOutline} className="me-2" />Расписание</h5>
                        <ScheduleTab task={task} />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {/* Чеклист - всегда видно */}
                        <h5 className="mb-3 mt-3">
                            <Icon icon={listOutline} className="me-2" />
                            Чеклист{' '}
                            <small className="text-secondary">
                                ({checklistItems.filter(i => i.isDone).length} / {checklistItems.length})
                            </small>
                        </h5>
                        <TaskChecklist key={`task-list-${taskUUID}`} task={task} fromDate={fromDate} part={part} checklistItems={checklistItems} onOpenTask={onOpenTask} onCloseModal={finalToggle} />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {/* Комментарии - всегда видно */}
                        <TaskComments task={task} />
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    );
}
